// AUDITORIA PONTA A PONTA DO CLICK-TO-CALL: o que aconteceu entre o gestor
// clicar em "ligar" dentro do HubSpot e a transcrição chegar de volta no CRM.
// Espelha /v1/click-to-call-audit no back e responde a duas perguntas:
//   - CASO A CASO (GET /v1/click-to-call-audit): a linha do tempo de cada
//     link emitido — abriu? discou? completou? transcreveu? entregou? — com os
//     eventos crus e a ligação resultante, quando houve;
//   - NO AGREGADO (GET /v1/click-to-call-audit/summary): o funil do período e
//     ONDE se perdeu, por estágio e por motivo.
// As duas leem a MESMA feature do back e compartilham `LossStage` (o filtro da
// tela caso a caso é a chave dos blocos "onde perdeu" da tela de saúde) — por
// isso moram juntas: separá-las daria dois arquivos que se importariam.
//
// O `CallStatus` vem emprestado de ./calls.js: o filtro de status da auditoria é
// o MESMO status da chamada, e duplicar a união aqui criaria duas listas do
// mesmo enum divergindo com o tempo.
//
// Fica de fora — de propósito — a GRAVAÇÃO e a TRANSCRIÇÃO da ligação auditada.
// Elas continuam em ./calls.js (getCallRecordingUrl / getCallTranscription):
// são o mesmo endpoint que o detalhe de chamada usa, e a tela de auditoria só
// as lê sob chaves de cache próprias (ver src/hooks/use-click-to-call-audit.ts).
// Também fica de fora o toggle /v1/sip/click-to-call, que liga a feature na org:
// ele nasceu no meio da seção da IA de Voz em api.ts e sai de lá com ela.
import { api, toApiError } from "./client.js";
import type { CallStatus } from "./calls.js";

export interface ClickToCallAuditEvent {
  id: string;
  event: string;
  callId: string | null;
  statusCode: number | null;
  errorCode: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface ClickToCallAuditItem {
  grantId: string;
  externalLeadId: string | null;
  leadHubspotUrl: string | null;
  phoneLast4: string | null;
  sellerName: string | null;
  sellerEmail: string | null;
  sellerHubspotUrl: string | null;
  createdAt: string;
  openedAt: string | null;
  sessionExpiresAt: string | null;
  events: ClickToCallAuditEvent[];
  call: {
    id: string;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    duration: number | null;
    hasRecording: boolean;
    hasTranscription: boolean;
  } | null;
}

export interface ClickToCallAuditResponse {
  items: ClickToCallAuditItem[];
  total: number;
  limit: number;
  offset: number;
}

export async function getClickToCallAudit(params: {
  q?: string;
  status?: CallStatus;
  stage?: LossStage;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<ClickToCallAuditResponse> {
  const { data, error } = await api<ClickToCallAuditResponse>("/v1/click-to-call-audit", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/click-to-call-audit", error);
  return data;
}

// Estágios de perda do funil do click-to-call — espelham LOSS_STAGES do back
// (features/click-to-call-audit/metrics.ts). Servem de filtro na auditoria e de
// chave dos blocos "onde perdeu" na tela de saúde.
export const LOSS_STAGES = [
  "not_opened",
  "not_dialed",
  "dial_failed",
  "call_not_completed",
  "not_transcribed",
  "not_delivered",
] as const;
export type LossStage = (typeof LOSS_STAGES)[number];

export interface ClickToCallFunnel {
  issued: number;
  opened: number;
  dialed: number;
  completed: number;
  transcribed: number;
  delivered: number;
}

export interface ClickToCallLoss {
  stage: LossStage;
  count: number;
  reasons: Array<{ code: string; count: number }>;
}

export interface ClickToCallHealthSummary {
  funnel: ClickToCallFunnel;
  pending: number;
  losses: ClickToCallLoss[];
  avgDurationSec: number | null;
  perDay: Array<{ day: string } & ClickToCallFunnel>;
}

// GET /v1/click-to-call-audit/summary — funil de entrega do click-to-call no
// período. Sem `status` de propósito: filtrar por status distorceria o funil.
export async function getClickToCallHealth(
  params: { from?: string; to?: string; q?: string } = {},
): Promise<ClickToCallHealthSummary> {
  const { data, error } = await api<ClickToCallHealthSummary>("/v1/click-to-call-audit/summary", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/click-to-call-audit/summary", error);
  return data;
}
